/**
 * Admin server functions for CMS documents and announcement banners.
 * Server-only by the same mechanism as lib/admin/server.ts: every handler
 * re-checks the caller with requirePlatformAdmin (a route beforeLoad guard
 * does NOT protect a server function).
 *
 * Document CRUD delegates to lib/cms/content-ops.server.ts — the shared core
 * behind this session surface AND the bearer-secret admin API
 * (routes/api/admin/content*) — so the two cannot drift. Announcements are
 * session-only and live here in full.
 */
import { createServerFn } from '@tanstack/react-start'
import { desc, eq } from 'drizzle-orm'
import { z } from 'zod'

import { db } from '@/db'
import { announcements } from '@/db/schema'
import { requirePlatformAdmin } from '@/lib/admin/require-admin'
import {
  ANNOUNCEMENT_LEVELS,
  type AnnouncementLevel,
  isIsoInstant,
  isWindowOrdered,
  UUID_RE,
} from '@/lib/cms/cms'
import {
  type AdminContentDetail,
  type AdminContentRow,
  deleteContentDoc,
  getContentDoc,
  listContentDocs,
  type SaveContentInput,
  saveContentDoc,
  setContentDocStatus,
} from '@/lib/cms/content-ops.server'
import { PublicError } from '@/lib/errors'
import { emitBusinessEvent } from '@/lib/telemetry/business.server'
import {
  optionalNote,
  parseInput,
  requiredText,
  id as zId,
} from '@/lib/validation'

// Defined here rather than imported from content-ops.server: this is a
// module-scope VALUE (saveAnnouncementSchema uses it), and importing one from
// a server-only module at module scope would drag that module into the client
// build, where import protection replaces it with an error mock.
const rowId = zId.regex(UUID_RE, 'Not found')

// Route files and the editor import these from here; the definitions live
// with the shared ops.
export type { AdminContentDetail, AdminContentRow, SaveContentInput }

// ---------------------------------------------------------------------------
// Documents (delegating to content-ops)
// ---------------------------------------------------------------------------

export const adminListContent = createServerFn({ method: 'GET' }).handler(
  async (): Promise<AdminContentRow[]> => {
    await requirePlatformAdmin()
    return listContentDocs()
  },
)

export const adminGetContent = createServerFn({ method: 'GET' })
  .validator((d: { id: string }) => d)
  .handler(async ({ data }): Promise<AdminContentDetail> => {
    await requirePlatformAdmin()
    return getContentDoc(data)
  })

export const adminSaveContent = createServerFn({ method: 'POST' })
  .validator((d: SaveContentInput) => d)
  .handler(async ({ data }): Promise<{ id: string }> => {
    const caller = await requirePlatformAdmin()
    return saveContentDoc(data, { via: 'admin', userId: caller.userId })
  })

export const adminSetContentStatus = createServerFn({ method: 'POST' })
  .validator((d: { id: string; status: 'draft' | 'published' }) => d)
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    return setContentDocStatus(data, { via: 'admin', userId: caller.userId })
  })

export const adminDeleteContent = createServerFn({ method: 'POST' })
  .validator((d: { id: string }) => d)
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    return deleteContentDoc(data, { via: 'admin', userId: caller.userId })
  })

// ---------------------------------------------------------------------------
// Announcements
// ---------------------------------------------------------------------------

export interface AdminAnnouncementRow {
  id: string
  message: string
  level: AnnouncementLevel
  linkHref: string | null
  linkLabel: string | null
  active: boolean
  startsAt: string | null
  endsAt: string | null
  dismissible: boolean
  updatedAt: string
}

export const adminListAnnouncements = createServerFn({
  method: 'GET',
}).handler(async (): Promise<AdminAnnouncementRow[]> => {
  await requirePlatformAdmin()
  const rows = await db
    .select()
    .from(announcements)
    .orderBy(desc(announcements.createdAt))
  return rows.map((r) => ({
    id: r.id,
    message: r.message,
    level: r.level,
    linkHref: r.linkHref,
    linkLabel: r.linkLabel,
    active: r.active,
    startsAt: r.startsAt?.toISOString() ?? null,
    endsAt: r.endsAt?.toISOString() ?? null,
    dismissible: r.dismissible,
    updatedAt: r.updatedAt.toISOString(),
  }))
})

/** Rejects zoneless datetime-local values; see isIsoInstant in lib/cms/cms. */
const isoInstant = z.string().refine(isIsoInstant, {
  message: 'Use an ISO timestamp with a timezone.',
})

const saveAnnouncementSchema = z
  .object({
    id: rowId.optional(),
    message: requiredText(300),
    level: z.enum(ANNOUNCEMENT_LEVELS),
    linkHref: optionalNote(300),
    linkLabel: optionalNote(60),
    active: z.boolean(),
    startsAt: isoInstant.nullish(),
    endsAt: isoInstant.nullish(),
    dismissible: z.boolean(),
  })
  .refine((d) => isWindowOrdered(d.startsAt, d.endsAt), {
    message: 'End must be after start.',
  })
  // (?!\/) rejects protocol-relative //host URLs, which would otherwise pass
  // as "internal" and be rendered as a same-tab, referrer-carrying anchor.
  .refine((d) => !d.linkHref || /^\/(?!\/)|^https:\/\//.test(d.linkHref), {
    message: 'Link must be an internal path or an https URL.',
  })

export type SaveAnnouncementInput = z.input<typeof saveAnnouncementSchema>

export const adminSaveAnnouncement = createServerFn({ method: 'POST' })
  .validator((d: SaveAnnouncementInput) =>
    parseInput(saveAnnouncementSchema, d),
  )
  .handler(async ({ data }): Promise<{ id: string }> => {
    const caller = await requirePlatformAdmin()
    const values = {
      message: data.message,
      level: data.level,
      linkHref: data.linkHref,
      linkLabel: data.linkHref ? data.linkLabel : null,
      active: data.active,
      startsAt: data.startsAt ? new Date(data.startsAt) : null,
      endsAt: data.endsAt ? new Date(data.endsAt) : null,
      dismissible: data.dismissible,
      updatedAt: new Date(),
      updatedByUserId: caller.userId,
    }
    let id = data.id
    if (id) {
      const [updated] = await db
        .update(announcements)
        .set(values)
        .where(eq(announcements.id, id))
        .returning({ id: announcements.id })
      if (!updated) throw new PublicError('Announcement not found', 404)
    } else {
      const [created] = await db
        .insert(announcements)
        .values(values)
        .returning({ id: announcements.id })
      if (!created) throw new PublicError('Could not create the announcement')
      id = created.id
    }
    emitBusinessEvent('cms.announcement_save', {
      announcement_id: id,
      level: data.level,
      active: data.active,
      created: !data.id,
      actor: 'admin',
      admin_id: caller.userId,
    })
    return { id }
  })

export const adminDeleteAnnouncement = createServerFn({ method: 'POST' })
  .validator((d: { id: string }) => parseInput(z.object({ id: rowId }), d))
  .handler(async ({ data }) => {
    const caller = await requirePlatformAdmin()
    const [deleted] = await db
      .delete(announcements)
      .where(eq(announcements.id, data.id))
      .returning({ id: announcements.id })
    if (!deleted) throw new PublicError('Announcement not found', 404)
    emitBusinessEvent('cms.announcement_delete', {
      announcement_id: data.id,
      actor: 'admin',
      admin_id: caller.userId,
    })
    return { ok: true }
  })
